import React from 'react';
import PropTypes from 'prop-types';
import { useLabels, usePrefs } from 'ws-scripts/modules/common';
import NavItemLabel from './NavItemLabel';

/**
 * DrilldownBackButton — back button row for the drilldown panel header.
 *
 * Renders a back button (arrow icon + sr-only label) and, when the
 * `showDrilldownCategoryName` pref is enabled, the parent item's label
 * alongside it.
 *
 * @param {Object} props
 * @param {React.RefObject} [props.backRef] - Ref forwarded to the button element for focus management
 * @param {() => void} props.onSubmenuClose - Called when the back button is clicked
 * @param {string} [props.categoryLabel] - Label of the active parent item (shown when pref is enabled)
 */
const DrilldownBackButton = ({ backRef, onSubmenuClose, categoryLabel }) => {
	const labels = useLabels();
	const backLabel = labels.get('BACK');
	const { showDrilldownCategoryName } = usePrefs();
	// !== 'disabled' is intentional: undefined means default-visible (opt-out pattern)
	const showCategoryName = showDrilldownCategoryName !== 'disabled';

	return (
		<div className="d-flex align-items-center">
			<button
				type="button"
				ref={backRef}
				className="btn btn-link p-0 text-decoration-none ws-nav-drawer__back"
				onClick={onSubmenuClose}
				data-testid="drilldown-back-button"
			>
				<i
					className="ddc-icon ddc-icon-skinny-arrow-left ddc-font-size-large d-flex align-items-center justify-content-center flex-shrink-0"
					aria-hidden="true"
				/>
				<span className="sr-only">{backLabel}</span>
			</button>
			{showCategoryName && categoryLabel && (
				<NavItemLabel
					label={categoryLabel}
					className="ws-nav-drawer__drilldown-category-name ml-3 ddc-font-size-small"
				/>
			)}
		</div>
	);
};

DrilldownBackButton.propTypes = {
	backRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
	onSubmenuClose: PropTypes.func.isRequired,
	categoryLabel: PropTypes.string
};

DrilldownBackButton.defaultProps = {
	backRef: null,
	categoryLabel: ''
};

export default DrilldownBackButton;
